'use client';

import * as React from 'react';
import { cn } from './cn';
import { useControllableState, useId } from './hooks';

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Text beside the switch. Clicking it toggles. */
  label?: React.ReactNode;
  /** Secondary line under the label. */
  description?: React.ReactNode;
  size?: 'sm' | 'md';
  /** Label side; default `start` puts text first in reading order and the switch at the end. */
  labelPlacement?: 'start' | 'end';
  /** Submitted with a form when set. */
  name?: string;
}

const TRACK = {
  sm: 'h-5 w-9',
  md: 'h-6 w-11',
} as const;
const THUMB = {
  sm: 'size-4 translate-x-0 rtl:-translate-x-0',
  md: 'size-5 translate-x-0 rtl:-translate-x-0',
} as const;
const THUMB_ON = {
  sm: 'translate-x-4 rtl:-translate-x-4',
  md: 'translate-x-5 rtl:-translate-x-5',
} as const;

/**
 * Boolean toggle with an immediate effect (no Save button). `k-hit` grows the
 * pointer target to 44 px on touch without changing the visual size.
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    defaultChecked = false,
    onCheckedChange,
    label,
    description,
    size = 'md',
    labelPlacement = 'start',
    name,
    className,
    disabled,
    id: idProp,
    ...props
  },
  ref,
) {
  const autoId = useId('switch');
  const id = idProp ?? autoId;
  const descId = `${id}-desc`;
  const [on, setOn] = useControllableState(checked, defaultChecked, onCheckedChange);

  const control = (
    <button
      ref={ref}
      id={id}
      type="button"
      role="switch"
      aria-checked={on}
      aria-describedby={description ? descId : undefined}
      disabled={disabled}
      onClick={() => setOn(!on)}
      className={cn(
        'k-hit inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5',
        'transition-colors duration-(--duration-base) ease-out-soft',
        'disabled:cursor-not-allowed disabled:opacity-45',
        on ? 'bg-accent' : 'bg-line-strong',
        TRACK[size],
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none rounded-full bg-surface-2 shadow-sm',
          'transition-transform duration-(--duration-base) ease-out-soft',
          THUMB[size],
          on && THUMB_ON[size],
        )}
      />
      {name ? <input type="hidden" name={name} value={on ? 'on' : ''} /> : null}
    </button>
  );

  if (!label && !description) return control;

  const text = (
    <span className="flex min-w-0 flex-col gap-0.5">
      {label ? (
        <label htmlFor={id} className="cursor-pointer text-sm font-medium text-ink">
          {label}
        </label>
      ) : null}
      {description ? (
        <span id={descId} className="text-xs text-ink-3">
          {description}
        </span>
      ) : null}
    </span>
  );

  return (
    <div className={cn('flex min-h-touch items-center justify-between gap-4')}>
      {labelPlacement === 'start' ? (
        <>
          {text}
          {control}
        </>
      ) : (
        <>
          {control}
          {text}
        </>
      )}
    </div>
  );
});
